import { differenceInMinutes, parseISO } from 'date-fns';
import removeMd from 'remove-markdown';
import {
  EVENT_LOCATION,
  EVENT_TITLE,
  EVENT_TYPE,
  FULL_EVENT_LOCATION,
} from '../app/constants/events';
import {
  allCourses,
  allEvents,
  allFAQs,
  allPrices,
  oneEvent,
} from '../app/constants/urls';
import { getAmoFormat, parseDate } from '../helpers/lib';

export const getProgramGroups = async (
  ac,
  program,
  location,
  noSlice = false,
) => {
  const url = `${process.env.GATSBY_LK_URL}/api/groups?${
    program ? `&courseType=${program}` : ''
  }${location ? `&location=${location}` : ''}`;

  try {
    const response = await fetch(url, { signal: ac.signal });
    const result = await response.json();

    const sortedData = result.sort((a, b) => {
      if (a.startDate < b.startDate) {
        return -1;
      }
      if (a.startDate > b.startDate) {
        return 1;
      }

      return 0;
    });
    if (noSlice) {
      return sortedData;
    }
    return sortedData.splice(0, 3);
  } catch (err) {
    console.error('Не удалось получить список потоков');
  }
};

export const getNearestGroups = async (ac, program, location) => {
  try {
    const groups = await getProgramGroups(ac, program, location);
    const nearestGroup = groups.find((item) => item.placesLeft);
    return nearestGroup;
  } catch (err) {
    console.error('Не удалось получить ближайший поток');
  }
};

export const getGroups = (ac) => {
  const url = `${process.env.GATSBY_API_URL}/groups?_sort=startOnlineDate:ASC`;
  return fetch(url, { signal: ac.signal }).then((response) => response.json());
};

export const getAiCourseGroups = async (ac) => {
  const url = `${process.env.GATSBY_API_URL}/groups?aiCourse=true&_sort=startOnlineDate:ASC`;
  try {
    const response = await fetch(url, { signal: ac.signal });
    const result = await response.json();
    return result;
  } catch (err) {
    console.error('Не удалось получить список AI потоков');
    return [];
  }
};

export const getPrices = async (ac, order = [0, 1, 2, 3, 4, 5, 6, 7, 8]) => {
  const response = await fetch(allPrices, { signal: ac.signal });
  const result = await response.json();
  return result.sort((a, b) => {
    const isOnlineA = a.productName.includes('online');
    const isPrepareA = a.productName.includes('prepare');
    const isDsA = a.productName.includes('ds');
    const isOnlineB = b.productName.includes('online');
    const isPrepareB = b.productName.includes('prepare');
    const isDsB = b.productName.includes('ds');

    if (!isOnlineA && !isPrepareA && (isOnlineB || isPrepareB)) {
      return -1;
    } else if ((isOnlineA || isPrepareA) && !isOnlineB && !isPrepareB) {
      return 1;
    } else if (isOnlineA && isPrepareB) {
      return -1;
    } else if (isPrepareA && isOnlineB) {
      return 1;
    } else if (isDsA && !isDsB) {
      return 1;
    } else if (!isDsA && isDsB) {
      return -1;
    }
    return 0;
  });
};

export const getStats = async () => {
  try {
    const statData = await fetch(`${process.env.GATSBY_API_URL}/stats`);
    const statDataJson = await statData.json();
    return statDataJson;
  } catch (error) {
    console.warn('Error on getAllEvents', error);
    return [];
  }
};

export const getEventData = async (event, AC) => {
  const response = await fetch(allEvents, { signal: AC.signal });
  const data = await response.json();
  const { date, url_slug } = data
    .filter(
      (item) =>
        differenceInMinutes(new Date(), parseISO(item.date)) <= 50 &&
        item.titleEnum === event,
    )
    .sort((a, b) => a.date - b.date)[0];
  const [, day, month] = parseDate(date);
  const eventData = [`${day} ${month}`, `/events/${url_slug}/`];
  return eventData;
};

export const getAllEvents = async (AC) => {
  try {
    const response = await fetch(allEvents, { signal: AC.signal });
    const events = await response.json();

    return events
      .filter(
        (event) => differenceInMinutes(new Date(), parseISO(event.date)) <= 50,
      )
      .map((event) => ({
        city: event.city,
        amoCity: getAmoFormat(event.city),
        id: event.id,
        url_slug: event.url_slug,
        title: event.customTitle || EVENT_TITLE[event.titleEnum],
        eventType: EVENT_TYPE[event.subTitleEnum],
        location: EVENT_LOCATION[event.city],
        date: parseDate(event.date),
        startTime: event.startTime,
        description: removeMd(event.description || ''),
        shortDescription: event.shortDescription,
        marketingLabel: event.marketingLabel?.toLowerCase(),
        filterKey: event.titleEnum,
        format: event.city === 'ONLINE' ? 'online' : 'offline',
        tags: event.tags,
        amoDate: event.date,
        titleEnum: event.titleEnum,
        subTitleEnum: event.subTitleEnum,
        external_url: event.external_url,
        program: event.program,
      }));
  } catch (error) {
    console.warn('Error on getAllEvents', error);
    return [];
  }
};

export const getEventInfo = async (eventID, AC) => {
  try {
    const isOldUrl = !isNaN(+eventID);
    let url;
    if (isOldUrl) {
      url = `${oneEvent}?id=${eventID}`;
    } else {
      url = `${oneEvent}?url_slug=${eventID}`;
    }
    const response = await fetch(url, { signal: AC.signal });
    const [event] = await response.json();

    if (
      !event ||
      !event.show ||
      (differenceInMinutes(new Date(), parseISO(event.date)) > 50 &&
        !event.repeat)
    ) {
      return null;
    }
    return {
      title: event.customTitle || EVENT_TITLE[event.titleEnum],
      eventType: EVENT_TYPE[event.subTitleEnum],
      location: EVENT_LOCATION[event.city],
      fullLocation: FULL_EVENT_LOCATION[event.city],
      titleEnum: event.titleEnum,
      subTitleEnum: event.subTitleEnum,
      city: event.city,
      amoCity: getAmoFormat(event.city),
      date: event.date,
      startTime: event.startTime,
      duration: event.duration,
      subTitle: event.subTitle,
      description: event.description,
      img: event.gallery?.url,
      youtube: event.youtube,
      mainSection: event.mainSection,
      necessaryThings: event.necessaryThings,
      format: event.city === 'ONLINE' ? 'online' : 'offline',
      tags: event.tags || '',
      urlSlug: event.url_slug,
      speakers: [
        {
          name: event.firstSpeakerName,
          title: event.firstSpeakerTitle,
          description: event.firstSpeakerDescription,
          photo: event.firstSpeakerPhoto?.url,
          previousJob: event.firstSpeakerPreviousJobTitle,
          currentJob: event.firstSpeakerCompanyJobTitle,
          companyLogo: event.firstSpeakerCompanyLogo,
        },
        {
          name: event.secondSpeakerName,
          title: event.secondSpeakerTitle,
          description: event.secondSpeakerDescription,
          photo: event.secondSpeakerPhoto?.url,
          previousJob: event.secondSpeakerPreviousJobTitle,
          currentJob: event.secondSpeakerCompanyJobTitle,
          companyLogo: event.secondSpeakerCompanyLogo,
        },
        {
          name: event.thirdSpeakerName,
          title: event.thirdSpeakerTitle,
          description: event.thirdSpeakerDescription,
          photo: event.thirdSpeakerPhoto?.url,
          previousJob: event.thirdSpeakerPreviousJobTitle,
          currentJob: event.thirdSpeakerCompanyJobTitle,
          companyLogo: event.thirdSpeakerCompanyLogo,
        },
        {
          name: event.fourthSpeakerName,
          title: event.fourthSpeakerTitle,
          description: event.fourthSpeakerDescription,
          photo: event.fourthSpeakerPhoto?.url,
          previousJob: event.fourthSpeakerPreviousJobTitle,
          currentJob: event.fourthSpeakerCompanyJobTitle,
          companyLogo: event.fourthSpeakerCompanyLogo,
        },
      ],
      eventSpeakers: event.speakers,
      program: event.program,
    };
  } catch (error) {
    return {};
  }
};

export const getAllFAQs = async (ac) => {
  const response = await fetch(allFAQs, { signal: ac.signal });
  const result = await response.json();
  return result;
};

export const getCourse = async (ac, productName) => {
  const oneCourseUrl = `${allCourses}?productName=${productName}`;
  const response = await fetch(oneCourseUrl, { signal: ac.signal });
  const result = await response.json();
  return result;
};

export const getBanners = async (ac) => {
  const bannersUrl = `${process.env.GATSBY_LK_URL}/api/banners`;
  const response = await fetch(bannersUrl, { signal: ac.signal });
  const result = await response.json();
  return result;
};

export const checkPromocode = async (ac, promocode) => {
  try {
    const basePrice = 109500;
    const baseDiscount = 10;
    let discount = 0;
    let newPrice = basePrice;

    const response = await fetch(
      `${process.env.GATSBY_LK_URL}/api/promocodes?code=${promocode}`,
      { signal: ac.signal },
    );
    const foundPromocode = await response.json();
    if (!foundPromocode?.discount) {
      return {
        success: false,
        newPrice: basePrice,
      };
    }

    const newDiscount = baseDiscount + foundPromocode.discount;
    const exlusivePromocodes = [
      'ИЮЛЬ',
      'ИЮЛЬ50',
      'июль',
      'июль50',
      '50AUGUST1',
    ];
    if (exlusivePromocodes.includes(promocode)) {
      discount = Math.min(newDiscount, 50);
    } else {
      discount = Math.min(newDiscount, 25);
    }

    newPrice = Math.floor(basePrice * (1 - discount / 100));

    return {
      success: true,
      newPrice,
    };
  } catch (error) {
    console.error('Error checking promocode:', error);
    return {
      success: false,
      newPrice: basePrice,
    };
  }
};
